
import jstz from 'jstz';

import { computed, defineComponent, PropType, toRef, watchEffect } from 'vue';

import {
  LABEL_CONFIRM,
  LABEL_REGISTRATION_TERMS,
  LABEL_REGISTRATION_TERMS_PRIVACY,
  LABEL_REGISTRATION_TERMS_PLEASE_ACCEPT,
  LABEL_REGISTRATION_TERMS_AGE,
  LABEL_REGISTRATION_TERMS_GDPR,
  LABEL_REGISTRATION_TERMS_ACCEPT,
  LABEL_SIGN_UP,
  LABEL_USERNAME_ALREADY_EXISTS,
  LABEL_USERNAME_INVALID,
  LABEL_THUNDERBIRD_REGISTRATION_INTRO_TITLE,
  LABEL_THUNDERBIRD_REGISTRATION_INTRO,
  LABEL_YOU_RECEIVED_AN_INVITATION_FROM_GROUP_ADMINISTRATOR_PART1,
  LABEL_YOU_RECEIVED_AN_INVITATION_FROM_GROUP_ADMINISTRATOR_PART2,
  LABEL_EXPIRED_GROUP_INVITATION,
} from '@/libs/i18nLabels';
import PwdEyeIcon from './pwd-eye-icon.vue';
import FontList from '../assets/js/fonts/font_list';
import { fontPrefs, fontDefault } from '../assets/js/fonts/font_options';
import { addQueryStringParams } from '../libs/urlUtils';
import { useLastNameValidation, useFirstNameValidation } from '../libs/nameValidation';
import useUserNameValidation from '../libs/userNameValidation';
import usePasswordValidation from '../libs/passwordValidation';
import { FormData, ServerValidationError } from '../libs/registrationState';
import PanelHeader from './panel-header.vue';
import focus from '../libs/focus';
import { translateFormLabel, FormLabel, LABEL_FIRSTNAME, LABEL_LASTNAME, LABEL_LOGIN, LABEL_FIELD_INVALID, LABEL_PASSWORD } from '../libs/form';

interface FormStateData {
  // Password
  passwordFieldType: string;

  // Font
  UIFontsPrefs: string;
  UIFontsDefault: string;

  userNameFocus: boolean;

  //
  registering: boolean;
  ageCheck: boolean;
  termsCheck: boolean;
}

export default defineComponent({
  components: {
    PwdEyeIcon,
    PanelHeader,
  },

  emits: ['formSubmitted', 'onBack'],

  props: {
    emailDomain: {
      type: String,
      required: true,
    },

    lookAndFeel: {
      type: String,
      required: true,
    },

    trial: {
      type: Boolean,
      default: false,
    },

    showBack: {
      type: Boolean,
      default: false,
    },

    trialDays: {
      type: String,
    },

    trialToken: {
      type: String,
    },

    source: {
      type: String,
    },

    groupInvitation: {
      type: String,
    },

    groupInfo: {
      type: Object,
    },

    serviceName: {
      type: String,
      required: true,
    },

    thunderbirdInfo: {
      type: Boolean,
      default: false,
    },

    passwordMaxLength: {
      type: Number,
      required: true,
    },

    passwordMinLength: {
      type: Number,
      required: true,
    },

    firstnameMinLength: {
      type: Number,
      required: true,
    },

    firstnameMaxLength: {
      type: Number,
      required: true,
    },

    lastnameMinLength: {
      type: Number,
      required: true,
    },

    lastnameMaxLength: {
      type: Number,
      required: true,
    },

    usernameMinLength: {
      type: Number,
      required: true,
    },

    usernameMaxLength: {
      type: Number,
      required: true,
    },

    prefilledParams: {
      type: Object,
      required: true,
    },

    termsUrl: {
      type: String,
      required: true,
    },

    privacyPolicyUrl: {
      type: String,
      required: false,
    },

    validationErrors: {
      type: Object as PropType<ServerValidationError[]>,
    },
  },

  setup(props) {
    const { name: lastname, nameMessage: lNameMessage, isNameInvalid: isLastNameInvalid, validate: validLastName } = useLastNameValidation(
      props.lastnameMinLength,
      props.lastnameMaxLength,
    );

    const { name: firstname, nameMessage: fNameMessage, isNameInvalid: isFirstNameInvalid, validate: validFirstName } = useFirstNameValidation(
      props.firstnameMinLength,
      props.firstnameMaxLength,
    );

    const { userName: username, isUserNameInvalid, userNameMessage, autoValidateUserName: validUserName } = useUserNameValidation(
      props.usernameMinLength,
      props.usernameMaxLength,
      props.emailDomain,
      props.lookAndFeel,
    );

    const {
      password,
      passwordConfirm: confirm,
      isPasswordInvalid: isPwdInvalid,
      isPasswordConfirmInvalid: isPwdConfirmInvalid,
      passwordMessage,
      validate: validPassword,
    } = usePasswordValidation(props.passwordMinLength);

    function displayServerValidationError(err: ServerValidationError) {
      const { code } = err;
      let { field = '' } = err;

      // Lower case field name
      field = field.toLowerCase();

      switch (field) {
        case 'firstname':
          isFirstNameInvalid.value = true;
          fNameMessage.value = { label: LABEL_FIELD_INVALID, params: [{ label: LABEL_FIRSTNAME }] };
          break;

        case 'lastname':
          isLastNameInvalid.value = true;
          lNameMessage.value = { label: LABEL_FIELD_INVALID, params: [{ label: LABEL_LASTNAME }] };
          break;

        case 'accountname':
          isUserNameInvalid.value = true;

          if (code === 'ACCOUNT_ALREADY_EXISTS') {
            userNameMessage.value = { label: LABEL_USERNAME_ALREADY_EXISTS };
          } else {
            userNameMessage.value = { label: LABEL_USERNAME_INVALID };
          }

          break;

        case 'password':
          isPwdInvalid.value = true;
          passwordMessage.value = { label: LABEL_FIELD_INVALID, params: [{ label: LABEL_PASSWORD }] };
          break;

        default:
          break;
      }
    }

    // server validation errors

    if (props.prefilledParams) {
      // eslint-disable-next-line vue/no-setup-props-destructure
      const prefilled = props.prefilledParams;
      firstname.value = `${prefilled.first_name || prefilled.firstname || ''}`;
      lastname.value = `${prefilled.last_name || prefilled.lastname || ''}`;
      username.value = `${(prefilled.email || '').split('@')[0]}`;

      password.value = `${prefilled.password || ''}`;
      confirm.value = `${prefilled.confirm || ''}`;

      if (firstname.value.length > 0) {
        validFirstName('');
      }

      if (lastname.value.length > 0) {
        validLastName('');
      }

      if (username.value.length > 0) {
        validUserName('');
      }

      if (password.value.length > 0) {
        validPassword('');
      }
    }

    const isGroupInviationInvalid = computed(() => {
      return props.groupInfo && (!props.groupInfo.groupName || props.groupInfo.valid === false);
    });

    // server validation errors
    const validationErrors = toRef(props, 'validationErrors');

    watchEffect(() => {
      if (validationErrors.value) {
        validationErrors.value.forEach((validationError) => {
          displayServerValidationError(validationError);
        });
      }
    });

    return {
      lastname,
      lNameMessage,
      isLastNameInvalid,
      validLastName,
      firstname,
      fNameMessage,
      isFirstNameInvalid,
      validFirstName,
      username,
      isUserNameInvalid,
      validUserName,
      userNameMessage,
      password,
      confirm,
      isPwdInvalid,
      isPwdConfirmInvalid,
      passwordMessage,
      validPassword,
      isGroupInviationInvalid,

      LABEL_THUNDERBIRD_REGISTRATION_INTRO_TITLE,
      LABEL_THUNDERBIRD_REGISTRATION_INTRO,
      LABEL_YOU_RECEIVED_AN_INVITATION_FROM_GROUP_ADMINISTRATOR_PART1,
      LABEL_YOU_RECEIVED_AN_INVITATION_FROM_GROUP_ADMINISTRATOR_PART2,
      LABEL_EXPIRED_GROUP_INVITATION,
      LABEL_SIGN_UP,
    };
  },

  data(): FormStateData {
    return {
      passwordFieldType: 'password',

      userNameFocus: false,

      // Font
      UIFontsPrefs: '',
      UIFontsDefault: '',

      //
      registering: false,
      termsCheck: false,
      ageCheck: false,
    };
  },

  mounted() {
    // Focus
    focus(this.$refs.firstnameField);

    /**
     * Browser infos
     */
    setTimeout(() => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      FontList.get(fontPrefs, (list: any) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.UIFontsPrefs = FontList.report(list.map((key: any) => key.value).join(), 31);
      });
    }, 500);

    setTimeout(() => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      FontList.get(fontDefault, (list: any) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.UIFontsDefault = FontList.report(list.map((key: any) => key.value).join(), 31);
      });
    }, 1000);
  },

  computed: {
    isPwdVisible() {
      return this.passwordFieldType === 'text';
    },

    firstNameLabel() {
      return this.$t(LABEL_FIRSTNAME);
    },

    lastNameLabel() {
      return this.$t(LABEL_LASTNAME);
    },

    passwordMessageLabel() {
      if (!this.passwordMessage || !this.passwordMessage.label) {
        return '&nbsp;'; // maintains  space
      }

      return translateFormLabel(this.passwordMessage, this.$t);
    },

    passwordLabel() {
      return this.$t(LABEL_PASSWORD).toString();
    },

    confirmLabel() {
      return this.$t(LABEL_CONFIRM).toString();
    },

    usernameLabel() {
      return this.$t(LABEL_LOGIN);
    },

    registerButtonLabel() {
      return this.$t(LABEL_SIGN_UP).toString();
    },

    registerAgeLabel() {
      return this.$t(LABEL_REGISTRATION_TERMS_AGE).toString();
    },

    termsLabel() {
      const urlTerms = addQueryStringParams(this.termsUrl, { src: (this.source || '').trim(), forcelg: this.$i18n.locale });

      let msg = `${this.$t(LABEL_REGISTRATION_TERMS_ACCEPT)} ${this.$t(LABEL_REGISTRATION_TERMS, [
        '',
        `<a href="${urlTerms}" target="_blank">`,
        '</a>',
      ])}`;

      if (this.privacyPolicyUrl) {
        const urlPrivacy = addQueryStringParams(this.privacyPolicyUrl, { src: (this.source || '').trim(), forcelg: this.$i18n.locale });
        const privacyLink = this.$t(LABEL_REGISTRATION_TERMS_PRIVACY, ['', `<a href="${urlPrivacy}" target="_blank">`, '</a>']);

        msg += `, ${privacyLink}`;
      }

      return msg;
    },

    discoverUrl() {
      const url = addQueryStringParams('/', { src: (this.source || '').trim(), forcelg: this.$i18n.locale });
      return url;
    },
  },

  methods: {
    onPwdEyeClick() {
      this.passwordFieldType = this.passwordFieldType === 'text' ? 'password' : 'text';
      setTimeout(() => {
        const pwdElement = this.$refs.passwordField as HTMLElement;
        pwdElement.focus();
      }, 1);
    },

    register() {
      this.valid();
    },

    translate(formLabel: FormLabel) {
      return translateFormLabel(formLabel, this.$t);
    },

    focusUsername() {
      this.userNameFocus = true;
    },

    blurUsername() {
      this.userNameFocus = false;
      return true;
    },
    /**
     * Validate all
     */
    async valid() {
      if (this.registering) {
        return;
      }

      this.registering = true;

      this.validFirstName('');
      this.validLastName('');
      this.validPassword('');
      // async user name validation
      await this.validUserName('');

      // focus
      if (this.isFirstNameInvalid) {
        focus(this.$refs.firstnameField);
      } else if (this.isLastNameInvalid) {
        focus(this.$refs.lastnameField);
      } else if (this.isLastNameInvalid) {
        focus(this.$refs.lastnameField);
      } else if (this.isUserNameInvalid) {
        focus(this.$refs.usernameField);
      } else if (this.isPwdConfirmInvalid) {
        //  password confirm before password
        focus(this.$refs.passwordConfirmField);
      } else if (this.isPwdInvalid) {
        focus(this.$refs.passwordField);
      }

      // invalid form
      if (this.isFirstNameInvalid || this.isLastNameInvalid || this.isUserNameInvalid || this.isPwdInvalid) {
        this.registering = false;
        return;
      }

      if (!this.termsCheck) {
        let message = `${this.$t(LABEL_REGISTRATION_TERMS_PLEASE_ACCEPT)} ${this.$t(LABEL_REGISTRATION_TERMS)}`;
        if (this.privacyPolicyUrl) {
          message += `, ${this.$t(LABEL_REGISTRATION_TERMS_PRIVACY)}`;
        }
        alert(message);
        this.registering = false;
        return;
      }

      if (!this.ageCheck) {
        const isConfirm = window.confirm(this.$t(LABEL_REGISTRATION_TERMS_GDPR, [this.serviceName, this.serviceName]));

        if (isConfirm) {
          this.ageCheck = true;
        }
        this.registering = false;
        return;
      }

      this.submitData();
    },

    async submitData() {
      // timezone
      const timezone = jstz.determine();
      let tz = '--';
      if (timezone) {
        tz = timezone.name();
      }

      // Form data
      const formObject: FormData = {
        username: this.username,
        mailDomain: this.emailDomain,
        firstname: this.firstname,
        lastname: this.lastname,
        password: this.password,

        customer: this.lookAndFeel,
        trialToken: this.trialToken,
        locale: this.$i18n.locale,

        UIFontsPrefs: this.UIFontsPrefs,
        UIFontsDefault: this.UIFontsDefault,

        groupInvitation: this.groupInvitation,

        timezone: tz,
        termsOfUseAccepted: true,
        source: this.source,
      };

      this.$emit('formSubmitted', formObject);
      this.registering = false;
    },
  },
});
